/**
 * Spectator saga: use same ControllerRemote + sagaGameReceiver as players.
 * Only difference: no game actions (no sagaGameSender), only chat via spectator_message.
 */

import { PayloadAction } from '@reduxjs/toolkit';
import { buffers, Channel, channel, EventChannel, eventChannel } from 'redux-saga';
import { call, cancel, fork, put, take, takeLatest } from 'redux-saga/effects';

import { ensureControllerWrapperConnected } from '~/app/controllerRemoteWrapper';
import { setConnected, setSpectatorStatus, startSpectator, stopSpectator } from '~/game';
import { sendSpectatorMessage } from '~/overlay';
import { GameState } from '~/server/src/dtos';
import { View } from '~/server/src/interfaces';
import { StateMachineValue } from '~/server/src/tmmodel';
import { actions as setupActions } from '~/setup';

import sagaGameReceiver from './sagaGameReceiver';

type ClientView = Pick<View, 'receiveMsg'>;

const spectatorControllerChannel = (
  onViewCreated: (view: ClientView) => void,
): EventChannel<{ state: StateMachineValue; params: unknown }> => {
  return eventChannel((emit) => {
    const view: ClientView = {
      receiveMsg: async (state: StateMachineValue, params: unknown) => {
        emit({ state, params });
      },
    };
    onViewCreated(view);
    return () => {
      /* spectator channel cleanup */
    };
  }, buffers.expanding<{ state: StateMachineValue; params: unknown }>(10));
};

function* spectatorSaga(action: PayloadAction<{ gameId: string; initialGameState: GameState }>) {
  const { gameId, initialGameState } = action.payload;

  yield put(setSpectatorStatus('connecting'));
  yield put(setupActions.setPlayerId(0));
  yield put(setupActions.setGameId(gameId));
  yield put(setupActions.setGameType(initialGameState.gameType));
  // Do not setConnected(true) here; set it only after spectate_joined to avoid race with mainSaga's finally.

  let view: ClientView | undefined;
  const ch: EventChannel<{ state: StateMachineValue; params: unknown }> = yield call(
    spectatorControllerChannel,
    (v: ClientView) => {
      view = v;
    },
  );

  const wrapper: Awaited<ReturnType<typeof ensureControllerWrapperConnected>> = yield call(
    ensureControllerWrapperConnected,
    {
      onTokenExpired: () => console.log('sagaSpectator: Token expired'),
      onReconnected: () => console.log('sagaSpectator: Reconnected'),
    },
  );
  if (!wrapper) {
    ch.close();
    yield put(setSpectatorStatus('error'));
    return;
  }

  let connected = wrapper.isConnected();
  if (!connected) {
    try {
      yield call([wrapper, wrapper.connect]);
      connected = wrapper.isConnected();
    } catch {
      ch.close();
      yield put(setSpectatorStatus('error'));
      return;
    }
  }

  const controller = wrapper.getController();
  if (!controller?.socket || !view) {
    ch.close();
    yield put(setSpectatorStatus('error'));
    return;
  }

  controller.playerView = view;

  const controlCh: Channel<'joined' | 'error'> = channel();
  controller.socket.emit('spectate', { gameId });
  controller.socket.once('spectate_joined', () => controlCh.put('joined'));
  controller.socket.once('spectate_error', () => controlCh.put('error'));

  const joinStatus: 'joined' | 'error' = yield take(controlCh);
  yield put(setSpectatorStatus(joinStatus));

  if (joinStatus === 'error') {
    ch.close();
    controlCh.close();
    yield put(setSpectatorStatus('idle'));
    return;
  }

  yield put(setConnected(true));

  // Wait for first message (joingame/startgame) so controller has real game/board/eventLog, then fork receiver.
  yield take(ch);

  const receiverTask = yield fork(sagaGameReceiver, controller, ch, []);

  // Listen for stop or spectator chat messages
  while (true) {
    const next: {
      type: string;
      payload?: { message: string };
    } = yield take([stopSpectator.type, sendSpectatorMessage.type]);
    if (next.type === stopSpectator.type) break;
    if (next.type === sendSpectatorMessage.type && next.payload?.message) {
      controller.socket.emit('spectator_message', {
        gameId,
        message: next.payload.message,
      });
    }
  }

  controller.socket.emit('spectate_leave', { gameId });
  yield cancel(receiverTask);
  ch.close();
  controlCh.close();
  yield put(setSpectatorStatus('idle'));
  yield put(setConnected(false));
}

export default function* spectatorSagaRoot() {
  yield takeLatest(startSpectator.type, spectatorSaga);
}
